import React, { useState } from 'react';
import AdminNav from '../components/AdminNav';
import './Admin.css';

const SHIFT_TIMES_REGULAR = ['7–10 AM', '10 AM–1 PM', '1–3 PM', '3–5 PM', '5–8 PM'];
const SHIFT_TIMES_SUNDAY  = ['7–10 AM', '10 AM–1 PM', '1–5 PM', '5–8 PM'];

function CheatSheet({ onLogout }) {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (key) => setOpenSection(s => s === key ? null : key);

  const Section = ({ id, title, children }) => (
    <div className="cheatsheet-section">
      <button className="cheatsheet-section-header" onClick={() => toggle(id)}>
        <span>{title}</span>
        <span>{openSection === id ? '▲' : '▼'}</span>
      </button>
      {openSection === id && <div className="cheatsheet-section-body">{children}</div>}
    </div>
  );

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Reference Sheet</h1>
        <p className="page-subtitle">Quick reference for the scheduling team — key info and phone scripts.</p>
      </header>

      <div className="scheduling-content">
        <div className="cheatsheet-wrapper">

          {/* Key info cards */}
          <div className="cheatsheet-info-grid">
            <div className="cheatsheet-info-card">
              <div className="cheatsheet-info-label">📍 Meeting Location (KH)</div>
              <div className="cheatsheet-info-value">4063 Jefferson Davis Hwy</div>
              <div className="cheatsheet-info-sub">Warrenville, SC 29851</div>
              <div className="cheatsheet-info-note">⚠️ Not the Augusta KH used in previous years</div>
            </div>
            <div className="cheatsheet-info-card">
              <div className="cheatsheet-info-label">⏰ Arrival Time</div>
              <div className="cheatsheet-info-value">1 hour before shift start</div>
              <div className="cheatsheet-info-sub">Volunteers must arrive early to be briefed and in position</div>
            </div>
            <div className="cheatsheet-info-card">
              <div className="cheatsheet-info-label">👥 Volunteers Per Shift</div>
              <div className="cheatsheet-info-value">5 per location</div>
              <div className="cheatsheet-info-sub">They rotate at the cart — not standing the full shift</div>
            </div>
            <div className="cheatsheet-info-card">
              <div className="cheatsheet-info-label">🧒 Children</div>
              <div className="cheatsheet-info-value">Underage not permitted</div>
              <div className="cheatsheet-info-sub">Only adult volunteers at the carts</div>
            </div>
          </div>

          {/* Shift times */}
          <div className="cheatsheet-section" style={{ marginBottom: '12px' }}>
            <div className="cheatsheet-section-header" style={{ cursor: 'default', justifyContent: 'flex-start', gap: '24px' }}>
              <span>🕐 Shift Times</span>
            </div>
            <div className="cheatsheet-section-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <div className="cheatsheet-shift-day">Thursday – Saturday (same each day)</div>
                <div className="cheatsheet-shift-list">
                  {SHIFT_TIMES_REGULAR.map(t => <div key={t} className="cheatsheet-shift-time">{t}</div>)}
                </div>
              </div>
              <div>
                <div className="cheatsheet-shift-day">Sunday (modified)</div>
                <div className="cheatsheet-shift-list">
                  {SHIFT_TIMES_SUNDAY.map(t => <div key={t} className="cheatsheet-shift-time">{t}</div>)}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                  Note: The 1–3 PM and 3–5 PM slots are combined into one 1–5 PM slot on Sunday.
                </div>
              </div>
            </div>
          </div>

          {/* Phone scripts */}
          <Section id="script-availability" title="📞 Phone Script — Calling for Availability">
            <div className="cheatsheet-script">
              <p><em>Use this when contacting a volunteer to collect their availability for the event days. The goal is to go through each day with them and record what works. Keep the tone warm and unhurried — this call shouldn't take long.</em></p>

              <div className="cheatsheet-script-block">
                <p>"Hi, may I speak with [name]? … Hi [name], my name is [your name] — I'm calling on behalf of the Scheduling Team for the SMPW Masters Event this year."</p>

                <p>"We received your information through your congregation's service committee — thank you so much for your interest in participating in this year's event. We really appreciate that!"</p>

                <p>"The way it works is that our team will go through the list of volunteers and schedule everyone based on the needs at each location. Once you're assigned to a shift, we'll reach out again to confirm it with you — so you'll have the opportunity to confirm or even cancel before the event if something comes up."</p>

                <p>"The reason for my call today is that before we can get you scheduled, we first need to know your general availability for each day of the event — April 9th through 12th. It shouldn't take too long — I'll just go through each day with you."</p>

                <p className="cheatsheet-script-note">
                  ℹ️ <strong>If they seem hesitant because they're not sure about their schedule:</strong><br />
                  "No worries at all — we understand things can change. We're just looking at your availability as of right now, to give us the best picture we can for scheduling. If something changes, or if you're assigned to a shift and find out you can't make it, you'll still be able to cancel when we contact you to confirm. So there's no pressure — just let us know what you think looks good for now."
                </p>

                <p><strong>Go through each day:</strong></p>
                <p>"Let's start with Thursday, April 9th — generally speaking, are you available that day? If so, roughly what part of the day works for you — morning, afternoon, evening, or any time?"</p>
                <p><em>[Repeat for Friday April 10, Saturday April 11, Sunday April 12]</em></p>

                <p>"Great, I have that noted. Once the schedule is finalized, someone from our team will contact you to confirm your specific shift."</p>

                <p>"That confirmation may come as an email or a message, so please keep an eye on your inbox — including your spam folder just in case."</p>

                <p>"Do you have any questions about the event or the shifts in general?"</p>

                <p className="cheatsheet-script-note">
                  ℹ️ <strong>Common questions — see the FAQ section below for full answers. Key points:</strong>
                  <br />• Shifts are at cart witnessing locations around the Augusta area
                  <br />• There are 5 volunteers per location per shift — they rotate, so no one is standing the entire time
                  <br />• Volunteers should arrive 1 hour before their shift start
                  <br />• Meeting location for briefing: 4063 Jefferson Davis Hwy, Warrenville SC (not the Augusta KH from prior years)
                  <br />• Children cannot participate — adults only at the carts
                </p>

                <p>"Thank you so much [name] — we really appreciate your willingness to help. We'll be in touch!"</p>
              </div>
            </div>
          </Section>

          <Section id="faq" title="❓ Frequently Asked Questions">
            <div className="cheatsheet-faq">
              {[
                {
                  q: 'Where do volunteers meet / assemble?',
                  a: '4063 Jefferson Davis Hwy, Warrenville, SC 29851. This is NOT the Augusta KH that was used in previous years — please make sure volunteers have the correct address.',
                },
                {
                  q: 'What time should volunteers arrive?',
                  a: 'Volunteers should plan to arrive 1 hour before their shift start time. For example, if their shift starts at 7 AM, they should arrive by 6 AM.',
                },
                {
                  q: 'Do they have to stand for the entire shift?',
                  a: 'No. There are 5 volunteers assigned per location per shift. They rotate at the cart, so while others are at the cart they are free to sit nearby or walk around.',
                },
                {
                  q: 'Can volunteers bring their children?',
                  a: 'Unfortunately no — only adult volunteers are permitted at the carts. Underage children cannot participate.',
                },
                {
                  q: 'What are the shift times?',
                  a: 'Thursday through Saturday have the same schedule: 7–10 AM, 10 AM–1 PM, 1–3 PM, 3–5 PM, and 5–8 PM. Sunday is slightly modified: 7–10 AM, 10 AM–1 PM, 1–5 PM (combined), and 5–8 PM.',
                },
                {
                  q: 'Can a volunteer do more than one shift?',
                  a: 'Yes, volunteers can serve multiple shifts. However, when scheduling, try to spread shifts across different volunteers so the load is balanced.',
                },
                {
                  q: 'What if a volunteer can no longer make their shift?',
                  a: 'Have them contact the scheduling team as soon as possible so a replacement can be arranged.',
                },
              ].map((item, i) => (
                <div key={i} className="cheatsheet-faq-item">
                  <div className="cheatsheet-faq-q">Q: {item.q}</div>
                  <div className="cheatsheet-faq-a">A: {item.a}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="script-confirm" title="📞 Phone Script — Shift Confirmation Call">
            <div className="cheatsheet-script">
              <p><em>Use this when confirming a volunteer's shift assignment closer to the event.</em></p>
              <div className="cheatsheet-script-block">
                <p>"Hi [name], this is [your name] from the SMPW scheduling team for the Augusta Masters event."</p>
                <p>"I'm calling to confirm your shift assignment. You're scheduled for [shift time] on [day] at the [location] location."</p>
                <p>"Please remember to arrive 1 hour early — so by [arrival time]."</p>
                <p>"The meeting location is 4063 Jefferson Davis Hwy in Warrenville, SC — that's different from the Augusta KH used in past years, so please make a note of that."</p>
                <p>"Do you have any questions or concerns? [address any]"</p>
                <p>"Thank you so much — we'll see you there!"</p>
              </div>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}

export default CheatSheet;
